<script lang="ts" setup>
import JIcon from "../icon/JIcon.vue";
import { useToastStore } from "./useToastStore";
const { toasts, position, removeToast } = useToastStore();

const iconMap = {
  success: "mdi:check-circle-outline",
  error: "mdi:close-circle-outline",
  warning: "mdi:alert-circle-outline",
  info: "mdi:information-outline",
};

const bgColorMap = {
  container: {
    success:
      "bg-teal-100 border-t-4 border-teal-500 rounded-b text-teal-900 dark:bg-teal-900/90 dark:text-teal-300 dark:border-teal-600",
    error:
      "text-red-800 border-t-4 border-red-500 bg-red-50 dark:bg-red-900/90 dark:text-red-300 dark:border-red-600",
    warning:
      "text-amber-800 border-t-4 border-amber-500 bg-amber-50 dark:bg-amber-900/90 dark:text-amber-300 dark:border-amber-600",
    info: "text-sky-800 border-t-4 border-sky-500 bg-sky-50 dark:bg-sky-900/90 dark:text-sky-300 dark:border-sky-600",
  },
  closeButton: {
    success:
      "hover:bg-teal-100 dark:hover:bg-teal-800/30 text-teal-600 dark:text-teal-400",
    error:
      "hover:bg-red-100 dark:hover:bg-red-800/30 text-red-600 dark:text-red-400",
    warning:
      "hover:bg-amber-100 dark:hover:bg-amber-800/30 text-amber-600 dark:text-amber-400",
    info: "hover:bg-sky-100 dark:hover:bg-sky-800/30 text-sky-600 dark:text-sky-400",
  },
  progress: {
    success: "bg-teal-500 dark:bg-teal-400",
    error: "bg-red-500 dark:bg-red-400",
    warning: "bg-amber-500 dark:bg-amber-400",
    info: "bg-sky-500 dark:bg-sky-400",
  },
};
</script>

<template>
  <div
    :class="{
      'top-4 right-4': position === 'top-right',
      'top-4 left-4': position === 'top-left',
      'bottom-4 right-4': position === 'bottom-right',
      'bottom-4 left-4': position === 'bottom-left',
    }"
    class="fixed z-50 space-y-3 max-w-md w-full"
  >
    <TransitionGroup name="toast" tag="div" class="relative">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="bgColorMap.container[toast.type]"
        :data-testid="`app-toast-${toast.type}`"
        class="relative overflow-hidden rounded-lg shadow-md transform transition-all duration-300 hover:shadow-lg cursor-pointer group"
        @click="removeToast(toast.id)"
      >
        <!-- Toast Content -->
        <div class="flex items-center justify-center px-2 py-3">
          <!-- Icon -->
          <div class="flex-shrink-0 mt-0.5">
            <JIcon :icon="iconMap[toast.type]" class="w-5 h-5" />
          </div>

          <!-- Message -->
          <div class="ml-3 flex-1">
            <p class="text-base font-medium leading-5">
              {{ toast.message }}
            </p>
          </div>

          <!-- Close Button -->
          <a
            :class="bgColorMap.closeButton[toast.type]"
            class="ml-4 flex-shrink-0 rounded-md p-1 transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-2 hover:cursor-pointer"
            @click.stop="removeToast(toast.id)"
          >
            <span class="sr-only">Close</span>
            <JIcon icon="mdi:close" class="w-4 h-4" />
          </a>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
/* Enter animations */
.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.toast-enter-to {
  opacity: 1;
  transform: translateX(0) scale(1);
}

.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Leave animations */
.toast-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
  position: absolute;
}

/* Move animation */
.toast-move {
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Progress bar animation */
.toast-progress {
  animation: progress linear forwards;
}

@keyframes progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
</style>
